/**
 * scheduleResolver.ts
 * -----------------------------------------------------------------------------
 * Client-side helper to resolve virtual check-in tasks from schedule configurations.
 * Uses the rrule library for RFC 5545 compliant recurrence rule parsing.
 */

import { RRule } from 'rrule';
import type { GlyphColor } from '../../../types/glyph';

/**
 * Check-in schedule configuration (from factor's checkInSchedule property)
 */
export interface CheckInScheduleConfig {
  /** Schedule source type */
  scheduleSource?: 'existing_timeframe' | 'recurring' | 'orbit';
  /** RRULE string for recurring schedules */
  rrule?: string;
  /** Rule configuration for CollectionSchedule format */
  rule?: {
    type: 'last_day' | 'last_thursday' | 'first_monday' | 'nth_day' | 'nth_weekday' | 'custom';
    config?: {
      nth?: number;
      weekday?: string;
      expression?: string;
    };
  };
  /** Start date (ISO 8601) */
  startDate?: string;
  /** End date (ISO 8601) */
  endDate?: string;
  /** Due time (HH:mm format) */
  dueTime?: string;
}

/**
 * Factor schedule info from the API
 */
export interface FactorScheduleInfo {
  factorId: string;
  sphereId: string;
  sphereName: string;
  sphereType?: string;
  glyph?: string;
  glyphColor?: GlyphColor;
  imageId?: string;
  checkInSchedule: CheckInScheduleConfig;
  metadata?: Record<string, unknown>;
  lastMaterializedDueAt?: string;
  /** Responsible user info (the user assigned to check in) */
  responsibleUser?: {
    id: string;
    name: string;
    glyph?: string;
    glyphColor?: GlyphColor;
    imageId?: string;
  };
}

/**
 * Resolved check-in occurrence
 */
export interface ResolvedOccurrence {
  dueAt: string; // ISO 8601
}

/**
 * Set time on a date (HH:mm format)
 */
function setTime(date: Date, timeStr: string): Date {
  const [hours, minutes] = timeStr.split(':').map(Number);
  const result = new Date(date);
  result.setHours(hours || 0, minutes || 0, 0, 0);
  return result;
}

/**
 * Get nth weekday of a month
 */
function getNthWeekdayOfMonth(year: number, month: number, dayOfWeek: number, nth: number): Date {
  if (nth > 0) {
    const firstDay = new Date(year, month, 1);
    const firstDayOfWeek = firstDay.getDay();
    let dayOffset = dayOfWeek - firstDayOfWeek;
    if (dayOffset < 0) dayOffset += 7;
    return new Date(year, month, 1 + dayOffset + (nth - 1) * 7);
  } else {
    const lastDay = new Date(year, month + 1, 0);
    const lastDayOfWeek = lastDay.getDay();
    let dayOffset = lastDayOfWeek - dayOfWeek;
    if (dayOffset < 0) dayOffset += 7;
    return new Date(year, month + 1, -dayOffset + (nth + 1) * 7);
  }
}

/**
 * Parse RRULE string and get occurrences within a date range
 */
function getRRuleOccurrences(rruleStr: string, rangeStart: Date, rangeEnd: Date): Date[] {
  try {
    const normalized = rruleStr.startsWith('RRULE:') ? rruleStr : `RRULE:${rruleStr}`;
    const baseRule = RRule.fromString(normalized);
    const rule = new RRule({
      ...baseRule.origOptions,
      dtstart: rangeStart,
    });
    return rule.between(rangeStart, rangeEnd, true);
  } catch (err) {
    console.error('[scheduleResolver] Failed to parse RRULE:', rruleStr, err);
    return [];
  }
}

/**
 * Resolve occurrences for CollectionSchedule format (rule-based)
 */
function resolveCollectionScheduleOccurrences(
  schedule: CheckInScheduleConfig,
  count: number,
  fromDate: Date
): ResolvedOccurrence[] {
  const occurrences: ResolvedOccurrence[] = [];
  const rule = schedule.rule;

  if (!rule?.type) {
    return occurrences;
  }

  const startDate = schedule.startDate ? new Date(schedule.startDate) : fromDate;
  const endDate = schedule.endDate ? new Date(schedule.endDate) : null;

  const current = new Date(Math.max(fromDate.getTime(), startDate.getTime()));
  current.setDate(1);

  const dayMap: Record<string, number> = {
    sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6,
  };

  const maxIterations = Math.max(12, count * 2);
  let iterations = 0;

  while (occurrences.length < count && iterations < maxIterations) {
    iterations++;
    let dueDate: Date | null = null;

    switch (rule.type) {
      case 'last_day':
        dueDate = new Date(current.getFullYear(), current.getMonth() + 1, 0, 17, 0, 0);
        break;

      case 'last_thursday':
        dueDate = getNthWeekdayOfMonth(current.getFullYear(), current.getMonth(), 4, -1);
        dueDate = setTime(dueDate, '17:00');
        break;

      case 'first_monday':
        dueDate = getNthWeekdayOfMonth(current.getFullYear(), current.getMonth(), 1, 1);
        dueDate = setTime(dueDate, '17:00');
        break;

      case 'nth_day': {
        const nth = rule.config?.nth || 1;
        const lastDayOfMonth = new Date(current.getFullYear(), current.getMonth() + 1, 0).getDate();
        const day = Math.min(nth, lastDayOfMonth);
        dueDate = new Date(current.getFullYear(), current.getMonth(), day, 17, 0, 0);
        break;
      }

      case 'nth_weekday': {
        const nth = rule.config?.nth || 1;
        const weekday = rule.config?.weekday || 'monday';
        const dayOfWeek = dayMap[weekday] ?? 1;
        dueDate = getNthWeekdayOfMonth(current.getFullYear(), current.getMonth(), dayOfWeek, nth);
        dueDate = setTime(dueDate, '17:00');
        break;
      }

      case 'custom':
      default:
        dueDate = new Date(current.getFullYear(), current.getMonth() + 1, 0, 17, 0, 0);
    }

    if (dueDate && dueDate >= startDate && (!endDate || dueDate <= endDate)) {
      occurrences.push({ dueAt: dueDate.toISOString() });
    }

    current.setMonth(current.getMonth() + 1);
  }

  return occurrences.slice(0, count);
}

/**
 * Resolve next check-in occurrences from a schedule configuration
 *
 * @param config - Schedule configuration
 * @param count - Number of occurrences to return
 * @param fromDate - Start date (default: now)
 * @returns Array of resolved occurrences
 */
export function resolveNextCheckInOccurrences(
  config: CheckInScheduleConfig,
  count = 10,
  fromDate?: Date
): ResolvedOccurrence[] {
  const now = fromDate || new Date();
  const occurrences: ResolvedOccurrence[] = [];

  // Handle CollectionSchedule format (has 'rule' property)
  if (config.rule && !config.scheduleSource) {
    return resolveCollectionScheduleOccurrences(config, count, now);
  }

  // Handle recurring RRULE schedule
  if ((config.scheduleSource === 'recurring' || !config.scheduleSource) && config.rrule) {
    const lookAheadMonths = Math.max(12, count);
    const rangeEnd = new Date(now);
    rangeEnd.setMonth(rangeEnd.getMonth() + lookAheadMonths);

    const rruleOccurrences = getRRuleOccurrences(config.rrule, now, rangeEnd);
    const dueTime = config.dueTime || '17:00';

    for (const occurrence of rruleOccurrences.slice(0, count)) {
      const dueDate = setTime(occurrence, dueTime);
      occurrences.push({ dueAt: dueDate.toISOString() });
    }
  }

  return occurrences.slice(0, count);
}

/**
 * Generate virtual tasks from factor schedules
 *
 * @param factorSchedules - Factor schedule info from API
 * @param existingTaskDueDates - Map of factorId -> Set of existing due dates (to avoid duplicates)
 * @param lookaheadDays - Days to look ahead
 * @returns Array of virtual task objects
 */
export function generateVirtualTasksFromSchedules(
  factorSchedules: FactorScheduleInfo[],
  existingTaskDueDates: Map<string, Set<string>>,
  lookaheadDays = 365,
  suppressedFactorIds?: ReadonlySet<string>
): Array<{
  id: string;
  taskType: 'check_in';
  name: string;
  dueAt: string;
  status: 'pending';
  sphereId: string;
  sphereName: string;
  sphereType?: string;
  glyph?: string;
  glyphColor?: GlyphColor;
  imageId?: string;
  isVirtual: true;
  factorId: string;
  factorName?: string;
  assignee?: {
    id: string;
    name: string;
    glyph?: string;
    glyphColor?: GlyphColor;
    imageId?: string;
  };
}> {
  const virtualTasks: Array<{
    id: string;
    taskType: 'check_in';
    name: string;
    dueAt: string;
    status: 'pending';
    sphereId: string;
    sphereName: string;
    sphereType?: string;
    glyph?: string;
    glyphColor?: GlyphColor;
    imageId?: string;
    isVirtual: true;
    factorId: string;
    factorName?: string;
    assignee?: {
      id: string;
      name: string;
      glyph?: string;
      glyphColor?: GlyphColor;
      imageId?: string;
    };
  }> = [];

  const now = new Date();
  const lookaheadEnd = new Date(now);
  lookaheadEnd.setDate(lookaheadEnd.getDate() + lookaheadDays);

  for (const schedule of factorSchedules) {
    try {
      // Skip factors with collapsed groups (e.g. groupCount > 1)
      if (suppressedFactorIds?.has(schedule.factorId)) {
        continue;
      }

      const existingDates = existingTaskDueDates.get(schedule.factorId) || new Set();

      // Skip virtual generation for factors that already have materialized tasks
      if (existingDates.size > 0) {
        continue;
      }

      // Determine start date for virtual tasks
      const startFrom = schedule.lastMaterializedDueAt
        ? new Date(new Date(schedule.lastMaterializedDueAt).getTime() + 1)
        : now;

      // Handle case where checkInSchedule might be double-stringified
      let checkInSchedule = schedule.checkInSchedule;
      if (typeof checkInSchedule === 'string') {
        try {
          checkInSchedule = JSON.parse(checkInSchedule);
        } catch {
          // Failed to parse, continue with original value
        }
      }

      // Resolve only the next upcoming occurrence per factor
      const occurrences = resolveNextCheckInOccurrences(
        checkInSchedule,
        1,
        startFrom
      );

      const factorName = (schedule.metadata as any)?.factorName ||
                         (schedule.metadata as any)?.name ||
                         'Check-in';

      for (const occurrence of occurrences) {
        const dueDate = new Date(occurrence.dueAt);

        // Skip if before now or beyond lookahead
        if (dueDate < now || dueDate > lookaheadEnd) {
          continue;
        }

        virtualTasks.push({
          id: `virtual-${schedule.factorId}-${dueDate.getTime()}`,
          taskType: 'check_in',
          name: schedule.sphereName || 'Check-in',
          dueAt: occurrence.dueAt,
          status: 'pending',
          sphereId: schedule.sphereId,
          sphereName: schedule.sphereName,
          sphereType: schedule.sphereType,
          glyph: schedule.glyph,
          glyphColor: schedule.glyphColor,
          imageId: schedule.imageId,
          isVirtual: true,
          factorId: schedule.factorId,
          factorName,
          assignee: schedule.responsibleUser,
        });

        // Only generate 1 virtual task per factor
        break;
      }
    } catch (err) {
      console.error('[scheduleResolver] Failed to process schedule:', schedule.factorId, err);
    }
  }

  return virtualTasks;
}
